import { useState } from 'react';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

/**
 * A non-blocking "recommended field is empty" hint, rendered as a field
 * end-adornment: an amber warning icon that toggles a tooltip on click
 * (click-driven so it works on touch). Render it only while the field is
 * actually empty.
 *
 * `bare` drops the InputAdornment wrapper — for placement inside an
 * Autocomplete's own endAdornment container (see mergeAutocompleteAdornment).
 */
export default function EmptyFieldWarning({
    message,
    bare = false
}: {
    message: string;
    bare?: boolean;
}) {
    const [open, setOpen] = useState(false);
    const icon = (
        <Tooltip title={message} open={open} onClose={() => setOpen(false)}>
            <IconButton
                size="small"
                edge={bare ? false : 'end'}
                aria-label={message}
                onClick={(event) => {
                    event.stopPropagation();
                    setOpen((prev) => !prev);
                }}
            >
                <WarningAmberIcon color="warning" fontSize="small" />
            </IconButton>
        </Tooltip>
    );
    if (bare) {
        return icon;
    }
    return (
        <InputAdornment
            position="end"
            // Stretch to the field's full height so the icon centers vertically
            // (default adornments top-align on multiline inputs).
            sx={{ alignSelf: 'stretch', maxHeight: 'none' }}
        >
            {icon}
        </InputAdornment>
    );
}
